import { saveAs } from 'file-saver';
import { action, computed, observable, runInAction } from 'mobx';
import {
    addToMap,
    bind,
    ConnectionManager,
    HttpClient,
    IAccessTokenStore,
    IConfig,
    ITimezoneStore,
    toArray,
} from 'Mri.Web.Common';
import { ActiveDisplay } from './ActiveDisplay';
import { ServiceKit } from '../ServiceKits/ServiceKitStore';
import { AppStore } from '../Core/AppStore';

export enum Labels {
    Offline = 'No or Limited Connectivity',
    Good = 'Good',
    Critical = 'Critical',
    Warning = 'Video On, Warning',
    Playing = 'Video On',
    NotPlaying = 'Video Off',
    TotalDisplays = 'Total Displays',
}

export interface SoftwareUpdateStatusPollLinkedManifests extends Mri.DisplaySoftware.SoftwareUpdateStatusPoll {
    linkedSoftwareUpdateManifests: LinkedSoftwareUpdateManifest[];
}

export interface LinkedSoftwareUpdateManifest {
    manifestGuid: string;
    manifestName: string;
}

export class DisplaysStore implements ITimezoneStore {
    public constructor(httpClient: HttpClient, accessTokenStore: IAccessTokenStore, appStore: AppStore) {
        this.httpClient = httpClient;
        this.accessTokenStore = accessTokenStore;
        this.appStore = appStore;

        this.connectionManager = new ConnectionManager('/sockets/display', accessTokenStore);
        const { connection } = this.connectionManager;
        connection.on('OnActiveDisplay', this.onActiveDisplay);
        connection.on('OnActiveDisplayStatus', this.onActiveDisplayStatus);
        connection.on('OnTelemetryPoll', this.onTelemetryPoll);
        connection.on('OnSoftwareVersionPoll', this.onSoftwareVersionPoll);
        connection.on('OnSoftwareUpdateStatusPoll', this.onSoftwareUpdateStatusPoll);
        connection.on('OnCurrentDisplayEvents', this.onCurrentDisplayEvents);
    }

    @bind
    @action
    public setActiveDisplay(display: ActiveDisplay) {
        this.activeDisplay = display;
    }

    @action
    public async loadAsync(serialNumber: string, config?: IConfig) {
        const hasStatus = this.displaysWithStatus.has(serialNumber);
        const getStatus = config?.params?.status;
        const newConfig = {
            ...config,
        };
        if (hasStatus && !getStatus) {
            newConfig.params = { ...newConfig.params, status: true };
        }

        const display = await this.httpClient.get<Mri.Web.Internal.Controllers.Displays.ActiveDisplayModel>(
            `/api/v1/displays/${serialNumber}`,
            newConfig
        );

        if (display && !hasStatus && getStatus) {
            this.displaysWithStatus.set(serialNumber, true);
        }

        if (display) this.displays.set(serialNumber, new ActiveDisplay(display));
    }

    @action
    public async loadDisplayLoginAsync(serialNumber: string, config?: IConfig) {
        try {
            const displayLogin = await this.httpClient.get<Mri.DisplaySystem.DisplayLoginPoll>(
                `/api/v1/displays/${serialNumber}/display-login`,
                config
            );
            if (displayLogin) this.displayLogins.set(serialNumber, displayLogin);
        } catch (e: any) {
            console.log(`Login not found for (${serialNumber})`);
        }
    }

    @action
    public async loadWhereSearchAsync(
        model: Mri.Web.Internal.Controllers.Displays.SearchModel,
        config?: IConfig
    ): Promise<Mri.WebApi.PagingResult<string> | undefined> {
        const result = await this.httpClient.post<
        Mri.WebApi.PagingResult<Mri.Web.Internal.Controllers.Displays.ActiveDisplayModel>
        >('/api/v1/displays/search', model, config);

        if (result) {
            const models = result.result;
            const serialNumbers = result.result.map(d => d.serialNumber);

            addToMap(
                this.displays,
                models,
                d => d.serialNumber,
                d => new ActiveDisplay(d)
            );

            if (model.status) models.forEach(d => this.displaysWithStatus.set(d.serialNumber, true));

            return {
                limit: result.limit,
                offset: result.offset,
                result: serialNumbers,
                returned: result.returned,
                total: result.total,
            };
        }
    }

    @action
    public async loadDisplayListAggregatesAsync(
        model: Mri.Web.Internal.Controllers.Displays.SearchModel,
        config?: IConfig
    ): Promise<Mri.Web.Common.Controllers.DisplayListAggregatesModel | undefined> {
        return this.httpClient.post<Mri.Web.Common.Controllers.DisplayListAggregatesModel>(
            '/api/v1/displays/aggregates',
            model,
            config
        );
    }

    @action
    public async loadGpsDataAsync(model: Mri.Web.Internal.Controllers.Displays.GpsSearchModel, config?: IConfig) {
        const gpsData = await this.httpClient.post<Mri.Web.Internal.Controllers.Displays.DisplayGpsModel[]>(
            `/api/v1/displays/gps-data`,
            model,
            config
        );
        if (gpsData) {
            this.gpsData = gpsData;
        }
    }

    @action
    public async loadDisplaySettingsAsync(serialNumber: string) {
        const displaySettings = await this.httpClient.get<any>(`/api/v1/displays/${serialNumber}/settings`);
        if (displaySettings) this.displaySettings.set(serialNumber, displaySettings);
    }

    public async sendDisplaySettingsAsync(serialNumber: string, display: Mri.DisplaySettings.Display) {
        await this.httpClient.post(`/api/v1/displays/${serialNumber}/settings/display`, display);
    }

    public async sendAuxDisplaySettingsAsync(serialNumber: string, auxDevice: Mri.DisplaySettings.AuxDevice) {
        await this.httpClient.post(
            `/api/v1/displays/${serialNumber}/settings/aux-devices/${auxDevice.deviceId}`,
            auxDevice
        );
    }

    @action
    public async loadSmartVuSettingsAsync(serialNumber: string) {
        const smartVuSettings = await this.httpClient.get<Mri.Displays.DisplaySmartVuSettings>(
            `/api/v1/displays/${serialNumber}/smartvu-settings`
        );
        if (smartVuSettings) this.smartVuSettings.set(serialNumber, smartVuSettings);
    }

    @action
    public async updateSmartVuSettingsAsync(serialNumber: string, model: Mri.Displays.DisplaySmartVuSettings) {
        const smartVuSettings = await this.httpClient.put<Mri.Displays.DisplaySmartVuSettings>(
            `/api/v1/displays/${serialNumber}/smartvu-settings`,
            model
        );

        if (smartVuSettings) this.smartVuSettings.set(serialNumber, smartVuSettings);
    }

    @action
    public async updateDisplayCommentsAsync(
        serialNumber: string,
        model: Mri.Web.Internal.Controllers.Displays.DisplayCommentsModel
    ) {
        await this.httpClient.put(`/api/v1/displays/${serialNumber}/comments`, model);
    }

    @action
    public async updateAsync(serialNumber: string, model: Mri.Web.Internal.Controllers.Displays.UpdateDisplayModel) {
        const display = await this.httpClient.put<Mri.Web.Internal.Controllers.Displays.ActiveDisplayModel>(
            `/api/v1/displays/${serialNumber}`,
            model
        );

        if (display) {
            if (!this.displaysWithStatus.has(serialNumber)) {
                this.displays.set(serialNumber, new ActiveDisplay(display));
            } else {
                await this.loadAsync(serialNumber);
            }
        }
    }

    @action
    public async loadAlertEventsAsync(serialNumber: string) {
        const events = await this.httpClient.get<Mri.Web.Internal.Controllers.Displays.DisplayEventModel[]>(
            `/api/v1/displays/${serialNumber}/display-events/alerts`
        );
        if (events) this.alertEvents.set(serialNumber, events);
    }

    @action
    public async loadCurrentDisplayEventsAsync(serialNumber: string) {
        let events = await this.httpClient.get<Mri.Web.Internal.Controllers.Displays.DisplayEventModel[]>(
            `/api/v1/displays/${serialNumber}/display-events/current`
        );

        events = events?.filter(e => e.details != 'WebApi Kicked');

        if (events) this.currentEvents.set(serialNumber, events);
    }

    @action
    public async loadSoftwareVersionsAsync(serialNumber: string) {
        const softwareVersions = await this.httpClient.get<Mri.DisplaySoftware.SoftwareVersion[]>(
            `/api/v1/displays/${serialNumber}/software`
        );
        if (softwareVersions) this.softwareVersions.set(serialNumber, softwareVersions);
    }

    @action
    public async loadDisplayEventProfilesAsync(serialNumber: string) {
        const profiles = await this.httpClient.get<Mri.DisplayEventProfiles.DisplayEventProfile[]>(
            `/api/v1/displays/${serialNumber}/display-event-profiles`
        );
        if (profiles) this.profiles.set(serialNumber, profiles);
    }

    @action
    public async loadCurrentStatusAsync(serialNumber: string) {
        const status = await this.httpClient.get<Mri.DisplayStatuses.ActiveDisplayStatus>(
            `/api/v1/displays/${serialNumber}/status/current`
        );
        if (status) this.statuses.set(serialNumber, status);
    }

    @action
    public async loadLatestScreenSnapshotAsync(serialNumber: string) {
        const screenSnapshots = await this.httpClient.get<Mri.Web.Internal.Controllers.Displays.ScreenSnapshotModel[]>(
            `/api/v1/displays/${serialNumber}/screen-snapshot/latest`
        );
        if (screenSnapshots) this.screenSnapshots.set(serialNumber, screenSnapshots);
    }

    @action
    public async loadCurrentScreenSnapshotAsync(serialNumber: string, manualLoad?: boolean) {
        let snapshotRequest = this.screenSnapshotRequests.get(serialNumber);
        if (snapshotRequest && !manualLoad) return;

        else {
            this.screenSnapshotRequests.set(serialNumber, true);

            try {
                const screenSnapshots = await this.httpClient.get<Mri.Web.Internal.Controllers.Displays.ScreenSnapshotModel[]>(
                    `/api/v1/displays/${serialNumber}/screen-snapshot/current`
                );

                if (screenSnapshots) this.screenSnapshots.set(serialNumber, screenSnapshots);
            } catch (e) {
                console.error(e);
            } finally {
                this.screenSnapshotRequests.set(serialNumber, false);

                if (this.appStore.gridRef) {
                    this.appStore.gridRef?.api.refreshCells();
                }
            }
        }
    }

    @action
    public async loadOpenTicketsAsync(serialNumber: string) {
        const tickets = await this.httpClient.get<Mri.Tickets.Ticket[]>(`/api/v1/displays/${serialNumber}/tickets`);
        if (tickets) this.openTickets.set(serialNumber, tickets);
    }

    @action
    public async loadSoftwareUpdateStatusAsync(serialNumber: string) {
        const updateStatus = await this.httpClient.get<SoftwareUpdateStatusPollLinkedManifests>(
            `/api/v1/displays/${serialNumber}/software/status`
        );
        if (updateStatus) this.updateStatuses.set(serialNumber, updateStatus);
    }

    @action
    public async clearSoftwareUpdateStatusAsync(serialNumber: string) {
        const updateStatus = await this.httpClient.put<SoftwareUpdateStatusPollLinkedManifests>(
            `/api/v1/displays/${serialNumber}/software/status/clear`
        );
        if (updateStatus) this.updateStatuses.set(serialNumber, updateStatus);
    }

    @action
    public async scheduleSoftwareUpdateStatusAsync(serialNumber: string, softwareUpdateManifestGuid: string) {
        const updateStatus = await this.httpClient.put<SoftwareUpdateStatusPollLinkedManifests>(
            `/api/v1/displays/${serialNumber}/software/schedule/${softwareUpdateManifestGuid}`
        );

        if (updateStatus) this.updateStatuses.set(serialNumber, updateStatus);
    }

    @action
    public async unscheduleSoftwareUpdateStatusAsync(serialNumber: string, softwareUpdateManifestGuid: string) {
        const updateStatus = await this.httpClient.delete<SoftwareUpdateStatusPollLinkedManifests>(
            `/api/v1/displays/${serialNumber}/software/schedule/${softwareUpdateManifestGuid}`
        );

        if (updateStatus) this.updateStatuses.set(serialNumber, updateStatus);
    }

    @action
    public async loadWarehouseAsync(serialNumber: string) {
        const warehouse = await this.httpClient.get<Mri.Warehouses.Warehouse>(
            `/api/v1/displays/${serialNumber}/warehouse`
        );
        if (warehouse) this.warehouses.set(serialNumber, warehouse);
    }

    @action
    public async loadDisplayConfigStatusAsync(serialNumber: string) {
        const configStatus = await this.httpClient.get<Mri.DisplayConfigurations.DisplayConfigStatusPoll>(
            `/api/v1/displays/${serialNumber}/config/status`
        );
        if (configStatus) this.configStatuses.set(serialNumber, configStatus);
    }

    @action
    public async loadAuditLogsAsync(serialNumber: string) {
        const auditLog = await this.httpClient.get<Mri.Audit.AuditLog<any>[]>(
            `/api/v1/displays/${serialNumber}/audit-log`
        );
        if (auditLog) this.auditLogs.set(serialNumber, auditLog);
    }

    @action
    public async loadDisplayActionLogsByTimeAsync(serialNumber: string, start: Date, end: Date) {
        const auditLog = await this.httpClient.get<Mri.Audit.AuditLog<any>[]>(
            `/api/v1/displays/${serialNumber}/audit-log/by-timestamp`,
            {
                params: {
                    startTime: start,
                    endTime: end,
                },
            }
        );
        if (auditLog) this.auditLogs.set(serialNumber, auditLog);
      
    }

    @action
    public async loadWarrantyAlertLogsAsync(serialNumber: string) {
        const warrantyAlertLog = await this.httpClient.get<Mri.Audit.AuditLog<Mri.Audit.WarrantyAlertLog>[]>(
            `/api/v1/displays/${serialNumber}/audit-log/warranty-alerts`
        );
        if (warrantyAlertLog) this.warrantyAlertLogs.set(serialNumber, warrantyAlertLog);
    }

    @action
    public async loadQuickAlertLogsAsync(serialNumber: string) {
        const quickAlertLog = await this.httpClient.get<Mri.Audit.AuditLog<Mri.Audit.WarrantyAlertLog>[]>(
            `/api/v1/displays/${serialNumber}/audit-log/quick-alerts`
        );
        if (quickAlertLog) this.quickAlertLogs.set(serialNumber, quickAlertLog);
    }

    @action
    public async loadEventStatusChangeLogsAsync(serialNumber: string, start: Date, end: Date) {
        const eventStatusChangeLogs = await this.httpClient.get<Mri.Audit.AuditLog<Mri.Audit.EventStatusChangeLog>[]>(
            `/api/v1/displays/${serialNumber}/audit-log/event-status-change-logs`,
            {
                params: {
                    startTime: start,
                    endTime: end,
                },
            }
        );
        
        if (eventStatusChangeLogs) this.eventStatusChangeLogs.set(serialNumber, eventStatusChangeLogs);
    }

    @action
    public async loadEventStatusChangeLogAsync(serialNumber: string, eventGuid: string) {
        const eventStatusChangeLog = await this.httpClient.get<Mri.Audit.AuditLog<Mri.Audit.EventStatusChangeLog>[]>(
            `/api/v1/displays/${serialNumber}/audit-log/event-status-change-logs/${eventGuid}`
        );

        if (eventStatusChangeLog) return eventStatusChangeLog;
        
        return [];
    }

    @action
    public async getLatestTelemetryPollAsync(serialNumber: string) {
        const poll = await this.httpClient.get<Mri.TelemetryPolls.TelemetryPoll>(
            `/api/v1/displays/${serialNumber}/telemetry/latest`,
            {
                params: {
                    interpolate: true,
                },
            }
        );

        if (poll) this.telemetryPolls.set(serialNumber, poll);
    }

    public async getEventHistoryAsync(serialNumber: string, start: Date, end: Date) {
        return this.httpClient.get<Mri.DisplayEvents.DisplayEvent[]>(
            `/api/v1/displays/${serialNumber}/display-events/log`,
            {
                params: {
                    endTime: end,
                    startTime: start,
                },
            }
        );
    }

    public async getStatusLogAsync(serialNumber: string, start: Date, end: Date) {
        return this.httpClient.get<Mri.DisplayStatuses.ActiveDisplayStatusLog[]>(
            `/api/v1/displays/${serialNumber}/status`,
            {
                params: {
                    endTime: end,
                    startTime: start,
                },
            }
        );
    }

    public async getTelemetryLogAsync(serialNumber: string, start: Date, end: Date) {
        return this.httpClient.get<Mri.TelemetryPolls.TelemetryPoll[]>(`/api/v1/displays/${serialNumber}/telemetry`, {
            params: {
                endTime: end,
                startTime: start,
            },
        });
    }

    public async getDisplayConfigLog(serialNumber: string, start: Date, end: Date) {
        return this.httpClient.get<Mri.DisplayConfigurations.DisplayConfigurationLog[]>(
            `/api/v1/displays/${serialNumber}/config/log`,
            {
                params: {
                    endTime: end,
                    startTime: start,
                },
            }
        );
    }

    public async exportTelemetryLogAsync(serialNumber: string, query: string) {
        const headers = new Headers();
        headers.append('Accept', 'text/csv');
        headers.append('Content-Type', 'application/json');
        headers.append('Authorization', 'Bearer ' + (await this.accessTokenStore.getAccessToken()));
        const response: Response = await fetch(`/api/v1/displays/${serialNumber}/telemetry/export?${query}`, {
            cache: 'default',
            credentials: 'include',
            headers,
            method: 'POST',
            mode: 'cors',
        });

        if (!response.ok) {
            throw new Error('Failed to export');
        }

        const blob = await response.blob();
        saveAs(blob, 'display_export.csv');
    }

    public async exportStatusLogAsync(serialNumber: string, query: string) {
        const headers = new Headers();
        headers.append('Accept', 'text/csv');
        headers.append('Content-Type', 'application/json');
        headers.append('Authorization', 'Bearer ' + (await this.accessTokenStore.getAccessToken()));
        const response: Response = await fetch(`/api/v1/displays/${serialNumber}/status/export?${query}`, {
            cache: 'default',
            credentials: 'include',
            headers,
            method: 'POST',
            mode: 'cors',
        });

        if (!response.ok) {
            throw new Error('Failed to export');
        }

        const blob = await response.blob();
        saveAs(blob, 'display_export.csv');
    }

    public async exportEventLogAsync(serialNumber: string, query: string) {
        const headers = new Headers();
        headers.append('Accept', 'text/csv');
        headers.append('Content-Type', 'application/json');
        headers.append('Authorization', 'Bearer ' + (await this.accessTokenStore.getAccessToken()));
        const response: Response = await fetch(`/api/v1/displays/${serialNumber}/display-events/export?${query}`, {
            cache: 'default',
            credentials: 'include',
            headers,
            method: 'POST',
            mode: 'cors',
        });

        if (!response.ok) {
            throw new Error('Failed to export');
        }

        const blob = await response.blob();
        saveAs(blob, 'display_export.csv');
    }

    public async exportConfigLogAsync(serialNumber: string, query: string) {
        const headers = new Headers();
        headers.append('Accept', 'text/csv');
        headers.append('Content-Type', 'application/json');
        headers.append('Authorization', 'Bearer ' + (await this.accessTokenStore.getAccessToken()));
        const response: Response = await fetch(`/api/v1/displays/${serialNumber}/config/export?${query}`, {
            cache: 'default',
            credentials: 'include',
            headers,
            method: 'POST',
            mode: 'cors',
        });

        if (!response.ok) {
            throw new Error('Failed to export');
        }

        const blob = await response.blob();
        saveAs(blob, 'display_export.csv');
    }

    public async exportActiveDisplayAsync(model: Mri.Web.Internal.Controllers.Displays.ActiveDisplayExportModel) {
        const headers = new Headers();
        headers.append('Accept', 'text/csv');
        headers.append('Content-Type', 'application/json');
        headers.append('Authorization', 'Bearer ' + (await this.accessTokenStore.getAccessToken()));
        const response: Response = await fetch(`/api/v1/csv/export-active-display`, {
            body: JSON.stringify(model),
            cache: 'default',
            credentials: 'include',
            headers,
            method: 'POST',
            mode: 'cors',
        });

        if (!response.ok) {
            throw new Error('Failed to export');
        }

        const blob = await response.blob();
        saveAs(blob, 'display_data.csv');
    }

    public async exportLocationDataAsync(displayFilter?: Mri.DisplayFilters.DisplayFilter) {
        const headers = new Headers();
        headers.append('Accept', 'text/csv');
        headers.append('Content-Type', 'application/json');
        headers.append('Authorization', 'Bearer ' + (await this.accessTokenStore.getAccessToken()));
        const response: Response = await fetch(`/api/v1/csv/export-locations`, {
            body: displayFilter ? JSON.stringify(displayFilter) : null,
            cache: 'default',
            credentials: 'include',
            headers,
            method: 'POST',
            mode: 'cors',
        });

        if (!response.ok) {
            throw new Error('Failed to export');
        }

        const blob = await response.blob();
        saveAs(blob, 'display_locations.csv');
    }

    public async exportMinMaxLogAsync(serialNumber: string, query: string) {
        const headers = new Headers();
        headers.append('Accept', 'text/csv');
        headers.append('Content-Type', 'application/json');
        headers.append('Authorization', 'Bearer ' + (await this.accessTokenStore.getAccessToken()));
        const response: Response = await fetch(`/api/v1/displays/${serialNumber}/min-max/export?${query}`, {
            cache: 'default',
            credentials: 'include',
            headers,
            method: 'POST',
            mode: 'cors',
        });

        if (!response.ok) {
            throw new Error('Failed to export');
        }

        const blob = await response.blob();
        saveAs(blob, 'display_export.csv');
    }

    public async saveAsync(models: Mri.Web.Internal.Controllers.Displays.UpdateDisplayModel[]) {
        await this.httpClient.post(`/api/v1/csv/save`, models);
    }

    public async getMinMaxLogAsync(serialNumber: string, start: Date, end: Date) {
        return this.httpClient.get<Mri.Web.Internal.Controllers.Displays.MinMaxModel[]>(
            `/api/v1/displays/${serialNumber}/min-max`,
            {
                params: {
                    endTime: end,
                    startTime: start,
                },
            }
        );
    }

    public async displayEventBatchActionAsync(
        serialNumber: string,
        model: Mri.Web.Internal.Controllers.Displays.BatchActionModel
    ) {
        await this.httpClient.put(`/api/v1/displays/${serialNumber}/display-events/bulk-keys`, model);
    }

    @action
    public async ignoreDisplayEvents(displayFilter?: Mri.DisplayFilters.DisplayFilter) {
        await this.httpClient.post('/api/v1/displays/bulk/ignore-all', displayFilter);
    }

    @action
    public async addFavoriteDisplay(serialNumber: string) {
        const display = await this.httpClient.post<Mri.Web.Internal.Controllers.Displays.ActiveDisplayModel>(
            `/api/v1/displays/${serialNumber}/favorite`
        );

        if (display) {
            if (!this.displaysWithStatus.has(serialNumber)) {
                this.displays.set(serialNumber, new ActiveDisplay(display));
            } else {
                await this.loadAsync(serialNumber);
            }
        }
    }

    @action
    public async removeFavoriteDisplay(serialNumber: string) {
        const display = await this.httpClient.delete<Mri.Web.Internal.Controllers.Displays.ActiveDisplayModel>(
            `/api/v1/displays/${serialNumber}/favorite`
        );

        if (display) {
            if (!this.displaysWithStatus.has(serialNumber)) {
                this.displays.set(serialNumber, new ActiveDisplay(display));
            } else {
                await this.loadAsync(serialNumber);
            }
        }
    }

    @action
    public async clearSoftwareUpdateStatusException(serialNumber: string): Promise<any> {
        return this.httpClient.put(`/api/v1/displays/${serialNumber}/status/clear-exception`);
    }

    @action
    public async createProxyAsync(serialNumber: string, service: string) {
        await this.httpClient.post(`/api/v1/displays/${serialNumber}/proxy`, undefined, { params: { service } });
    }

    @action
    public async subscribeToDisplayAsync(serialNumber: string) {
        await this.connectionManager.connection.send('SubscribeToDisplay', serialNumber);
    }

    @action
    public async unsubscribeToDisplayAsync(serialNumber: string) {
        await this.connectionManager.connection.send('UnsubscribeToDisplay', serialNumber);
    }

    public async refresh(serialNumber: string) {
        await this.connectionManager.connection.send('FullRefresh', serialNumber);
    }

    @bind
    @action
    public async getAssociatedServiceKits(serialNumber: string) {
        this.loadingDisplayServiceKits = true;
        await this.httpClient
            .get(`/api/v1/service-kits/display-association/${serialNumber}`)
            .then(
                action(response => {
                    this.displayServiceKits.set(serialNumber, response as ServiceKit[]);
                })
            )
            .catch(error => console.error(error))
            .finally(action(() => (this.loadingDisplayServiceKits = false)));
        return this.displayServiceKits.get(serialNumber);
    }

    @bind
    @action
    public async addServiceKitsToDisplay(serialNumber: string, serviceKitGuids: string[]) {
        this.loadingDisplayServiceKits = true;
        this.httpClient
            .put(`/api/v1/service-kits/display-association/${serialNumber}`, { serviceKitGuids })
            .then(
                action(response => {
                    this.displayServiceKits.set(serialNumber, response as ServiceKit[]);
                })
            )
            .catch(error => console.error(error))
            .finally(action(() => (this.loadingDisplayServiceKits = false)));
    }

    @bind
    @action
    public async removeServiceKitFromDisplay(displayGuid: string, serviceKitGuid: string) {
        this.loadingDisplayServiceKits = true;
        this.httpClient
            .delete(`/api/v1/service-kits/display-association/${displayGuid}/${serviceKitGuid}`)
            .then(
                action(response => {
                    this.displayServiceKits.set(displayGuid, response as ServiceKit[]);
                })
            )
            .catch(error => console.error(error))
            .finally(action(() => (this.loadingDisplayServiceKits = false)));
    }
    
    // Display Actions
    @action
    public async kickWebApiForDisplays(serialNumbers: string[]) {
        await this.httpClient.post('/api/v1/displays/trigger-web-api-kick-for-displays', serialNumbers);
    }

    @action
    public async rebootDisplays(serialNumbers: string[]) {
        await this.httpClient.post('/api/v1/displays/trigger-reboot-for-displays', serialNumbers);
    }

    @action
    public async enableStandbyModeForDisplays(serialNumbers: string[]) {
        await this.httpClient.post('/api/v1/displays/enable-standby-for-displays', serialNumbers);
    }

    @action
    public async disableStandbyModeForDisplays(serialNumbers: string[]) {
        await this.httpClient.post('/api/v1/displays/disable-standby-for-displays', serialNumbers);
    }

    @action
    public async restartServerForDisplays(serialNumbers: string[]) {
        await this.httpClient.post('/api/v1/displays/restart-server-for-displays', serialNumbers);
    }

    @bind
    @action
    private onClose(e: Error | undefined) {
        if (e) {
            console.error('onClose', e);
        } else {
            console.debug('onClose disconnected');
        }
    }

    @bind
    @action
    private onActiveDisplay(model: Mri.Web.Internal.Controllers.Displays.ActiveDisplayModel) {
        if (this.appStore.isIdle) return; // Skip processing if idle
        // reuse aggregates from existing active display
        const currentDisplay = this.displays.get(model.serialNumber);
        const newDisplay = new ActiveDisplay(model);
        newDisplay.aggregates = currentDisplay?.aggregates;
        this.activeDisplay = newDisplay;

        this.displays.set(model.serialNumber, newDisplay);

        if (this.appStore.gridRef && !this.appStore.isModalOpen) {
            //this.appStore.gridRef?.api.refreshInfiniteCache();
        }
    }

    @bind
    @action
    private onActiveDisplayStatus(model: Mri.DisplayStatuses.ActiveDisplayStatus) {
        if (this.appStore.isIdle) return; // Skip processing if idle
        this.statuses.set(model.displaySerialNumber, model);
    }

    @bind
    private onTelemetryPoll(model: Mri.TelemetryPolls.TelemetryPoll) {
        if (this.appStore.isIdle) return; // Skip processing if idle
    }

    @bind
    @action
    private onSoftwareVersionPoll(model: Mri.DisplaySoftware.SoftwareVersionPoll) {
        if (this.appStore.isIdle) return; // Skip processing if idle
        this.softwareVersions.set(model.displaySerialNumber, model.softwareVersions);

    }

    @bind
    @action
    private onSoftwareUpdateStatusPoll(model: SoftwareUpdateStatusPollLinkedManifests) {
        if (this.appStore.isIdle) return; // Skip processing if idle
        this.updateStatuses.set(model.displaySerialNumber, model);
    }

    @bind
    @action
    private onCurrentDisplayEvents(
        serialNumber: string,
        model: Mri.Web.Internal.Controllers.Displays.DisplayEventModel[]
    ) {
        if (this.appStore.isIdle) return; // Skip processing if idle
        this.currentEvents.set(serialNumber, model);
    }
    
    @observable
    public displays = new Map<string, ActiveDisplay>();
    @observable
    public activeDisplay: ActiveDisplay | null = null;
    @observable
    public displaysWithStatus = new Map<string, boolean>();
    @observable
    public updateStatuses = new Map<string, SoftwareUpdateStatusPollLinkedManifests>();
    @observable
    public configStatuses = new Map<string, Mri.DisplayConfigurations.DisplayConfigStatusPoll>();
    @observable
    public auditLogs = new Map<string, Mri.Audit.AuditLog<Mri.Audit.FileSystemUpdateLog>[]>();
    @observable
    public warrantyAlertLogs = new Map<string, Mri.Audit.AuditLog<Mri.Audit.WarrantyAlertLog>[]>();
    @observable
    public quickAlertLogs = new Map<string, Mri.Audit.AuditLog<Mri.Audit.WarrantyAlertLog>[]>();
    @observable
    public eventStatusChangeLogs = new Map<string, Mri.Audit.AuditLog<Mri.Audit.EventStatusChangeLog>[]>();
    @observable
    public currentEvents = new Map<string, Mri.Web.Internal.Controllers.Displays.DisplayEventModel[]>();
    @observable
    public alertEvents = new Map<string, Mri.Web.Internal.Controllers.Displays.DisplayEventModel[]>();
    @observable
    public gpsData: Mri.Web.Internal.Controllers.Displays.DisplayGpsModel[] = [];
    @observable
    public displaySettings = new Map<string, Mri.DisplaySettings.Settings>();
    @observable
    public smartVuSettings = new Map<string, Mri.Displays.DisplaySmartVuSettings>();
    @observable
    public softwareVersions = new Map<string, Mri.DisplaySoftware.SoftwareVersion[]>();
    @observable
    public profiles = new Map<string, Mri.DisplayEventProfiles.DisplayEventProfile[]>();
    @observable
    public statuses = new Map<string, Mri.DisplayStatuses.ActiveDisplayStatus>();
    @observable
    public telemetryPolls = new Map<string, Mri.TelemetryPolls.TelemetryPoll>();
    @observable
    public screenSnapshots = new Map<string, Mri.Web.Internal.Controllers.Displays.ScreenSnapshotModel[]>();
    @observable
    public openTickets = new Map<string, Mri.Tickets.Ticket[]>();
    @observable
    public displayLogins = new Map<string, Mri.DisplaySystem.DisplayLoginPoll>();
    @observable
    public warehouses = new Map<string, Mri.Warehouses.Warehouse>();
    @observable
    public screenOrientations = new Map<string, boolean>();
    @observable
    public displayServiceKits = new Map<string, ServiceKit[]>();
    @observable
    public screenSnapshotRequests = new Map<string, boolean>();
    @observable
    public loadingDisplayServiceKits = false;
    public readonly connectionManager: ConnectionManager;

    @computed
    public get timezones(): Map<string, string | undefined> {
        return new Map(Array.from(this.displays, ([k, v]) => [k, v.timezone]));
    }
    @computed
    public get activeDisplayServiceKitsList(): ServiceKit[] {
        return this.displayServiceKits.get(this.activeDisplay?.serialNumber ?? '') ?? [];
    }
    
    private readonly httpClient: HttpClient;
    private readonly accessTokenStore: IAccessTokenStore;
    private readonly appStore: AppStore;
}
